import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { ServiceRequestValidation } from 'src/app/models/service-request-token.model';
import { ServiceModel } from 'src/app/models/service.model';
import { ClientModel } from 'src/app/models/client.model';
import { AddressModel } from 'src/app/models/address.model';
import { ProductRequestModel } from 'src/app/models/product-request.model';

@Injectable({
  providedIn: 'root',
})
export class ServiceRequestService {
  private readonly configSvc = inject(ConfigurationService);

  private readonly http: HttpClient;
  private readonly storageKey = 'service_request_token';
  private accessToken?: string;

  constructor() {
    const handler = inject(HttpBackend);

    this.http = new HttpClient(handler);
    this.accessToken = sessionStorage.getItem(this.storageKey) ?? undefined;
  }

  validateRequestToken(token: string): Observable<ServiceRequestValidation> {
    const url = `${this.configSvc.notificationApiUrl}api/Campaigns/tokens/${token}/validate`;

    return this.http
      .post<ServiceRequestValidation>(url, null, {
        headers: this.buildHeaders(false),
      })
      .pipe(tap((response) => this.persistToken(response.access_token)));
  }

  markTokenAsUsed(token: string): Observable<void> {
    const url = `${this.configSvc.notificationApiUrl}api/Campaigns/tokens/${token}/use`;

    return this.http.post<void>(url, null, {
      headers: this.buildHeaders(),
    });
  }

  getServiceDetails(serviceId: string): Observable<ServiceModel> {
    const url = `${this.configSvc.productApiUrl}api/Services/${serviceId}`;
    return this.http.get<ServiceModel>(url, {
      headers: this.buildHeaders(),
    });
  }

  getClientByEmail(email: string): Observable<ClientModel> {
    const url = `${this.configSvc.workloadsApiUrl}api/Clients/`;
    const params = new HttpParams().set('email', email);
    return this.http.get<ClientModel>(url, {
      params,
      headers: this.buildHeaders(),
    });
  }

  getClientAddresses(clientId: string): Observable<AddressModel[]> {
    const url = `${this.configSvc.workloadsApiUrl}api/Clients/${clientId}/addresses`;
    return this.http.get<AddressModel[]>(url, {
      headers: this.buildHeaders(),
    });
  }

  createProductRequest(model: ProductRequestModel): Observable<any> {
    const url = `${this.configSvc.workloadsApiUrl}api/workloads/service-offer`;
    return this.http.post(url, model, {
      headers: this.buildHeaders(),
    });
  }

  clearSession(): void {
    sessionStorage.removeItem(this.storageKey);
    this.accessToken = undefined;
  }

  private persistToken(token: string): void {
    this.accessToken = token;
    sessionStorage.setItem(this.storageKey, token);
  }

  // We have to manually build the headers as we're not logged in
  private buildHeaders(includeAuth: boolean = true): HttpHeaders {
    let headers = new HttpHeaders({
      ClientTimeZone: this.getClientTimeZone(),
    });

    if (includeAuth && this.accessToken) {
      headers = headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    return headers;
  }

  private getClientTimeZone(): string {
    const offset = -new Date().getTimezoneOffset();
    const hours = this.padLeft(Math.floor(offset / 60).toString(), '0', 2);
    const minutes = this.padLeft(Math.floor(offset % 60).toString(), '0', 2);
    return `${offset >= 0 ? '+' : ''}${hours}:${minutes}`;
  }

  private padLeft(text: string, padChar: string, size: number): string {
    return (String(padChar).repeat(size) + text).substr(size * -1, size);
  }
}

